// AuthCallback — handles the redirect from auth.emergentagent.com.
// URL lands here as /auth/callback#session_id=<id>. We exchange that
// for our own JWT cookie and bounce to /dashboard.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { googleExchange } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError('No session token returned from Google.');
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const u = await googleExchange(sessionId);
        // One-time Complete Account (password) step for Google users without a
        // LearnixTa password; then un-onboarded users → /signup path picker;
        // onboarded users → dashboard. Accounts are matched by verified email —
        // no duplicates are created.
        const candidate = u?.return_to || '/dashboard';
        const dest = candidate.startsWith('/') && !candidate.startsWith('//') && !candidate.includes('\\') ? candidate : '/dashboard';
        // Clean the fragment so a reload doesn't try to re-exchange.
        window.history.replaceState({}, document.title, dest);
        navigate(dest, { replace: true });
      } catch (e) {
        setError(e.response?.data?.detail || e.message || 'Google sign-in failed.');
      }
    })();
  }, [googleExchange, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
      <div className="text-center" data-testid="auth-callback">
        {error ? (
          <>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">Sign-in failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/signin', { replace: true })}
              className="btn-primary"
              data-testid="auth-callback-back-btn"
            >
              Back to Sign In
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-teal-600 mb-4" />
            <p className="text-gray-700 font-medium">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
